import { useEffect, useRef } from 'react'

/**
 * Referans ekranlardaki `fitInsp()` karşılığı (CLAUDE.md §3.3).
 *
 * Yaptığı tek iş: sağ inceleme panelinin **kendi** yüksekliğini görünür alana
 * sığdırmak. Gövdenin kayması CSS'ten geliyor — `.insp{display:flex}` +
 * `.insp-b{flex:1; overflow-y:auto}`; yani footer (Kaydet / Kopyala / Tam sayfa)
 * kendiliğinden altta kalıyor, taşan kısım gövdede kayıyor.
 *
 * ÖNCEKİ SÜRÜM PANELİ ÇÖKERTİYORDU. `.insp-b`'ye `maxHeight = insp.clientHeight
 * − başlık − footer` yazıyordu; ama `.list-grid{align-items:start}` olduğu için
 * panelin yüksekliği İÇERİĞİNDEN geliyor. Gövdeyi kısaltmak paneli kısaltıyor,
 * kısalan panel bir sonraki ölçümde daha küçük bir `clientHeight` veriyor ve
 * ResizeObserver döngüyü yeniden tetikliyordu: panel ekranda yer varken
 * 120px tabanına kadar iniyor, içerik gereksiz yere kayıyordu.
 *
 * Referans doğru olanı yapıyor ve ölçüyü DIŞARIDAN alıyor: pencere yüksekliği
 * eksi panelin tepesi. Bu değer panelin kendi boyutuna bağlı olmadığı için
 * geri besleme yok.
 */
export function useFitInsp<T extends HTMLElement>() {
  const panel = useRef<T | null>(null)

  useEffect(() => {
    const el = panel.current
    if (!el) return

    // Sığdırma: pencere − panelin tepesi − 32px alt boşluk (referansla aynı).
    // Taban 220px: çok kısa pencerede panel okunmaz hâle gelmesin.
    const sigdir = () => {
      const tepe = el.getBoundingClientRect().top
      el.style.maxHeight = `${Math.max(220, window.innerHeight - tepe - 32)}px`
    }

    sigdir()
    // Yazı tipleri ve ilk yerleşim oturduktan sonra bir kez daha — referans da
    // aynı sebeple ikinci ölçümü yapıyor.
    const zaman = window.setTimeout(sigdir, 80)

    // Panel `position:sticky`; içerik kaydıkça tepesi değişiyor, ölçü tazelenmeli.
    const kaydirilan = el.closest('.content') ?? document.querySelector('.content')
    kaydirilan?.addEventListener('scroll', sigdir, { passive: true })
    window.addEventListener('resize', sigdir)

    // Satır seçimi paneli baştan çiziyor; yeni içerikle tepe değişebilir.
    // Yalnız childList/subtree dinleniyor: `style` bir ÖZNİTELİK değişimi
    // olduğu için kendi yazdığımız maxHeight bu gözlemciyi tetiklemiyor.
    const icerik = new MutationObserver(sigdir)
    icerik.observe(el, { childList: true, subtree: true })

    return () => {
      window.clearTimeout(zaman)
      kaydirilan?.removeEventListener('scroll', sigdir)
      window.removeEventListener('resize', sigdir)
      icerik.disconnect()
    }
  }, [])

  return panel
}
